import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FieldError } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import Chatbot from '../../../types/Chatbot';
import {
  PATH_VISIBILITY_MODE_LABELS,
  PathVisibilityMode
} from '../../../types/PathVisibilityMode';
import SettingsService from '../../../services/SettingsService/SettingsService';
import LabelWithInfo from '../../../components/LabelWithInfo/LabelWithInfo';
import CustomInput from '../../../components/CustomInput/CustomInput';
import Button from '../../../components/Button/Button';
import isUrl from '../../../utils/isUrl';
import isQueryOrFragmentOnly from '../../../utils/isQueryOrFragmentOnly';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select';

interface PathVisibilitySectionProps {
  readonly chatbot: Chatbot;
  readonly refreshChatbots: () => Promise<void>;
  readonly onSuccess: (message: string) => void;
  readonly onError: (message: string) => void;
}

interface PathRow {
  id: string;
  value: string;
}

function toRows(paths: string[]): PathRow[] {
  return paths.map((value) => ({ id: uuidv4(), value }));
}

function pathError(path: string): FieldError | undefined {
  if (isQueryOrFragmentOnly(path)) {
    return {
      type: 'validate',
      message: 'Enter the path without # or ?, e.g. /checkout'
    };
  }
  const trimmed = path.trim();
  if (trimmed !== '' && (isUrl(trimmed) || !trimmed.startsWith('/'))) {
    return { type: 'validate', message: 'Enter a path only, e.g. /help' };
  }
  return undefined;
}

function PathVisibilitySection({
  chatbot,
  refreshChatbots,
  onSuccess,
  onError
}: PathVisibilitySectionProps): React.ReactElement {
  const [mode, setMode] = React.useState<PathVisibilityMode>(
    chatbot.pathVisibilityMode
  );
  const [rows, setRows] = React.useState<PathRow[]>(() =>
    toRows(chatbot.pathRules)
  );
  const [isSaving, setIsSaving] = React.useState(false);

  // Compared by value: the chatbot list refreshes with new objects after unrelated settings change,
  // which must not discard unsaved paths.
  const savedRulesKey = JSON.stringify(chatbot.pathRules);
  React.useEffect(() => {
    setMode(chatbot.pathVisibilityMode);
    setRows(toRows(JSON.parse(savedRulesKey)));
  }, [chatbot.id, chatbot.pathVisibilityMode, savedRulesKey]);

  const restricted = mode !== PathVisibilityMode.ALL;
  const trimmedPaths = rows.map((row) => row.value.trim());
  const hasBlankPath = restricted && trimmedPaths.includes('');
  const hasInvalidPath = restricted && trimmedPaths.some(pathError);
  const isUnchanged =
    mode === chatbot.pathVisibilityMode &&
    (!restricted ||
      (trimmedPaths.length === chatbot.pathRules.length &&
        trimmedPaths.every((p, i) => p === chatbot.pathRules[i])));
  const canSave =
    !isSaving &&
    !isUnchanged &&
    !hasBlankPath &&
    !hasInvalidPath &&
    (!restricted || trimmedPaths.length > 0);

  const handleModeChange = (value: string): void => {
    const nextMode = value as PathVisibilityMode;
    setMode(nextMode);
    if (nextMode !== PathVisibilityMode.ALL && rows.length === 0) {
      setRows(toRows(['']));
    }
  };

  const handlePathChange = (id: string, value: string): void => {
    setRows(rows.map((row) => (row.id === id ? { ...row, value } : row)));
  };

  const handleAddPath = (): void => {
    setRows([...rows, ...toRows([''])]);
  };

  const handleRemovePath = (id: string): void => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;

    try {
      setIsSaving(true);
      await SettingsService.updatePathVisibility(
        chatbot.id,
        mode,
        restricted ? trimmedPaths : []
      );
      await refreshChatbots();
      onSuccess('Page visibility updated');
    } catch (err: any) {
      onError(err.message || 'Something went wrong');
    } finally {
      setIsSaving(false);
    }
  };

  const infoText =
    'Choose which pages of your website the chatbot appears on. ' +
    'Paths match whole sections, so /blog covers /blog and everything under it. ' +
    'Applies to the script embed.';

  return (
    <div className='w-full max-w-md border-t mt-8 pt-6'>
      <div className='space-y-2'>
        <LabelWithInfo htmlFor='path-visibility-mode' info={infoText}>
          Page visibility
        </LabelWithInfo>
        <Select value={mode} onValueChange={handleModeChange}>
          <SelectTrigger id='path-visibility-mode' className='w-full'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(PathVisibilityMode).map((value) => (
              <SelectItem key={value} value={value}>
                {PATH_VISIBILITY_MODE_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {restricted && (
        <div className='mt-4 space-y-2'>
          {rows.map((row) => (
            <div key={row.id} className='flex gap-2 items-center'>
              <CustomInput
                value={row.value}
                onChange={(e) => handlePathChange(row.id, e.target.value)}
                placeholder='/example/path'
                maxLength={1000}
                className='flex-1'
                containerClassName='mb-0 flex-1'
                disabled={isSaving}
                error={pathError(row.value)}
              />
              <button
                type='button'
                aria-label='Remove path'
                onClick={() => handleRemovePath(row.id)}
                className='text-red-500 hover:text-red-700'
                disabled={isSaving}
              >
                <Trash2 className='h-4 w-4' />
              </button>
            </div>
          ))}
          <button
            type='button'
            onClick={handleAddPath}
            className='py-1 flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm'
            disabled={isSaving}
          >
            <Plus className='h-4 w-4' />
            Add path
          </button>
          <p className='text-sm text-muted-foreground'>
            Each path matches itself and everything beneath it, e.g. /blog also
            matches /blog/my-post.
          </p>
        </div>
      )}

      <div className='flex justify-end mt-4'>
        <Button
          type='button'
          loading={isSaving}
          disabled={!canSave}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}

export default PathVisibilitySection;
